import { z } from "zod";
import { NodeFactSchema } from "./ideation-maps.js";
/**
 * Subject associations — Subject ↔ operational-object M2M.
 *
 * Distinct from `sourceRefs` (evidence/provenance on the graph node).
 * Phase 3 `rel` is `relates-to` only. `evidence-for` is intentionally
 * absent: a vault document may be evidence, associated, or both, but
 * those writes are explicit and separate.
 */
export const SUBJECT_ASSOCIATION_OBJECT_TYPES = [
    "vault_document",
    "workspace_document",
    "project",
    "meeting",
    "note",
    "contact",
    "company",
];
export const SubjectAssociationObjectTypeSchema = z.enum(SUBJECT_ASSOCIATION_OBJECT_TYPES);
/** Phase 3 vocabulary — explicit staff relate only. */
export const SUBJECT_ASSOCIATION_RELS = ["relates-to"];
export const SubjectAssociationRelSchema = z.enum(SUBJECT_ASSOCIATION_RELS);
export const SubjectAssociationSchema = z.object({
    id: z.string().uuid(),
    subjectId: z.string().uuid(),
    objectType: SubjectAssociationObjectTypeSchema,
    objectId: z.string().uuid(),
    rel: SubjectAssociationRelSchema,
    createdAt: z.string().datetime(),
    createdBy: z.string().email(),
    title: z.string(),
    href: z.string(),
});
export const SubjectAssociationListSchema = z.object({
    associations: z.array(SubjectAssociationSchema),
});
export const SubjectAssociationCreateSchema = z.object({
    objectType: SubjectAssociationObjectTypeSchema,
    objectId: z.string().uuid(),
});
export const SubjectObjectAssociationsQuerySchema = z.object({
    objectType: SubjectAssociationObjectTypeSchema,
    objectId: z.string().uuid(),
});
export const SubjectCaptureFactSchema = NodeFactSchema.pick({
    key: true,
    value: true,
    unit: true,
    source: true,
    asOf: true,
    note: true,
});
export const SubjectCaptureQuestionSchema = z.object({
    text: z.string().min(1).max(300),
    summary: z.string().max(4000).optional(),
});
export const SubjectCaptureDecisionSchema = z.object({
    text: z.string().min(1).max(4000),
    detail: z.string().max(20_000).optional(),
});
export const SubjectCaptureNoteSchema = z.object({
    title: z.string().min(1).max(300),
    body: z.string().max(50_000).default(""),
});
export const SubjectCaptureQuestionResultSchema = z.object({
    questionNodeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    rel: z.literal("questions"),
});
export const SubjectCaptureNoteResultSchema = z.object({
    noteId: z.string().uuid(),
    association: SubjectAssociationSchema,
});
export const SubjectIdParamSchema = z.object({
    subjectId: z.string().uuid(),
});
export const AssociationIdParamSchema = z.object({
    id: z.string().uuid(),
});
export const SUBJECT_ASSOCIATION_OBJECT_RESOURCE = {
    vault_document: "documents",
    workspace_document: "documents",
    project: "projects",
    meeting: "meetings",
    note: "notes",
    contact: "contacts",
    company: "companies",
};
export function subjectAssociationHref(objectType, objectId, title) {
    switch (objectType) {
        case "vault_document":
            return title ? `/documents?q=${encodeURIComponent(title)}` : "/documents";
        case "workspace_document":
            return "/documents?mode=working";
        case "project":
            return `/projects/${objectId}`;
        case "meeting":
            return "/meetings";
        case "note":
            return `/notes/${objectId}`;
        case "contact":
            return `/contacts/${objectId}`;
        case "company":
            return `/companies/${objectId}`;
    }
}
//# sourceMappingURL=subject-associations.js.map